import { useMemo } from 'react'

export type ChangePercentColor = 'text-low-em' | 'text-success-high-em' | 'text-danger-high-em'

export interface ChangePercentResult {
  label: string | null
  color: ChangePercentColor
  isPriceUp: boolean
  isPriceDown: boolean
}

type PriceType = number | null | undefined

export const computeChangePercent = (
  sellPrice: PriceType,
  openPrice: PriceType,
  showSign = true
): ChangePercentResult => {
  if (sellPrice == null || !openPrice || openPrice === 0) {
    return { label: null, color: 'text-low-em', isPriceUp: false, isPriceDown: false }
  }

  const changePercent = ((sellPrice - openPrice) / openPrice) * 100
  const sign = showSign ? (changePercent > 0 ? '+' : changePercent < 0 ? '-' : '') : ''
  const label = `${sign}${Math.abs(changePercent).toFixed(2)}%`
  const color: ChangePercentColor =
    changePercent > 0
      ? 'text-success-high-em'
      : changePercent < 0
      ? 'text-danger-high-em'
      : 'text-low-em'

  const isPriceUp = changePercent > 0
  const isPriceDown = changePercent < 0

  return { label, color, isPriceUp, isPriceDown }
}

export const useChangePercent = (
  sellPrice: PriceType,
  openPrice: PriceType
): ChangePercentResult => {
  return useMemo(() => computeChangePercent(sellPrice, openPrice), [sellPrice, openPrice])
}

export interface ChangePercentsResult {
  sellPrice: PriceType
  openPrice: PriceType
}

export const useChangePercents = (
  changePercents: ChangePercentsResult[]
): ChangePercentResult[] => {
  return useMemo(
    () =>
      changePercents.map(({ sellPrice, openPrice }) => computeChangePercent(sellPrice, openPrice)),
    [changePercents]
  )
}
